import * as React from 'react';
import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Select,
  Card as AntCard,
  Alert,
  Input,
  Empty,
  Radio,
} from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import {
  useZoomAccounts,
  useZoomMutations,
  useAccountGroups,
} from 'core/hooks';
import { formatWarsaw } from 'core/utils/zoomTime';

const REFETCH_DELAY = 3_000;

type AccountFilter = 'all' | 'with' | 'without';

export const Accounts = memo(() => {
  const { t } = useTranslation();

  const {
    data: accounts,
    loading,
    error: accountsError,
    refetch: refetchAccounts,
  } = useZoomAccounts();
  const mutations = useZoomMutations();
  const {
    groupsByAccountId,
    unassignedGroups,
    setUnassignedSearch,
    error: groupsError,
    refetch: refetchGroups,
  } = useAccountGroups();

  const [filter, setFilter] = useState<AccountFilter>('all');

  const screenError = accountsError ?? groupsError;
  const busy = mutations.setGroupAccount.loading;

  const accountOptions = useMemo(
    () =>
      accounts.map((account) => ({
        value: account.id,
        label: account.displayName,
      })),
    [accounts],
  );

  const unassignedOptions = useMemo(
    () =>
      unassignedGroups.map((group) => ({
        value: group.id,
        label: group.name,
      })),
    [unassignedGroups],
  );

  const filteredAccounts = useMemo(
    () =>
      accounts.filter((account) => {
        if (filter === 'all') {
          return true;
        }
        const hasGroups = Boolean(groupsByAccountId[account.id]?.length);
        return filter === 'with' ? hasGroups : !hasGroups;
      }),
    [accounts, filter, groupsByAccountId],
  );

  const refetchSoon = useCallback(() => {
    window.setTimeout(refetchAccounts, REFETCH_DELAY);
  }, [refetchAccounts]);

  const onSync = useCallback(async () => {
    await mutations.syncAccounts.run();
    refetchSoon();
  }, [mutations, refetchSoon]);

  const assign = useCallback(
    async (groupId: string, accountId?: string) => {
      const ok = await mutations.setGroupAccount.run(groupId, accountId);
      if (ok) {
        refetchGroups();
      }
    },
    [mutations, refetchGroups],
  );

  return (
    <Wrapper>
      {screenError ? (
        <Alert
          type="error"
          showIcon
          message={screenError}
          style={{ marginBottom: 12 }}
        />
      ) : null}
      <Header>
        <Title>{t('pages.zoom.accounts.title')}</Title>
        <Button
          type="primary"
          loading={mutations.syncAccounts.loading}
          onClick={onSync}
        >
          {t('pages.zoom.accounts.sync')}
        </Button>
      </Header>

      <Radio.Group
        optionType="button"
        buttonStyle="solid"
        value={filter}
        onChange={(event) => setFilter(event.target.value as AccountFilter)}
        style={{ marginBottom: 16 }}
      >
        <Radio.Button value="all">
          {t('pages.zoom.accounts.filterAll')}
        </Radio.Button>
        <Radio.Button value="with">
          {t('pages.zoom.accounts.filterWithGroups')}
        </Radio.Button>
        <Radio.Button value="without">
          {t('pages.zoom.accounts.filterWithoutGroups')}
        </Radio.Button>
      </Radio.Group>

      {!loading && !accounts.length ? (
        <Muted>{t('pages.zoom.accounts.empty')}</Muted>
      ) : null}
      {accounts.length && !filteredAccounts.length ? (
        <Muted>{t('pages.zoom.accounts.filterEmpty')}</Muted>
      ) : null}

      <AccountList>
        {filteredAccounts.map((account) => {
          const boundGroups = groupsByAccountId[account.id] ?? [];
          const hasGroups = boundGroups.length > 0;
          return (
            <AccountCard key={account.id} size="small" hasGroups={hasGroups}>
              <AccountHead>
                <Dot active={account.isActive} />
                <AccountName>{account.displayName}</AccountName>
                <CountBadge has={hasGroups}>
                  {t('pages.zoom.accounts.groupsCount', {
                    count: boundGroups.length,
                  })}
                </CountBadge>
              </AccountHead>
              <AccountMeta>
                <AccountEmail>{account.email}</AccountEmail>
                <AccountState>
                  {account.isActive
                    ? t('pages.zoom.accounts.active')
                    : t('pages.zoom.accounts.inactive')}
                </AccountState>
                <AccountSynced>
                  {t('pages.zoom.accounts.synced')}:{' '}
                  {formatWarsaw(account.syncedAt)}
                </AccountSynced>
              </AccountMeta>

              <GroupsLabel>{t('pages.zoom.accounts.boundGroups')}</GroupsLabel>
              {hasGroups ? (
                <GroupChips>
                  {boundGroups.map((group) => (
                    <GroupChip key={group.id}>
                      <span>{group.name}</span>
                      <ChipButton
                        type="button"
                        title={t('pages.zoom.accounts.unbindGroup')}
                        disabled={busy}
                        onClick={() => assign(group.id, undefined)}
                      >
                        <CloseOutlined />
                      </ChipButton>
                    </GroupChip>
                  ))}
                </GroupChips>
              ) : (
                <Muted small>{t('pages.zoom.accounts.noBoundGroups')}</Muted>
              )}

              <Select<string>
                showSearch
                style={{ width: '100%', marginTop: 'auto', paddingTop: 10 }}
                placeholder={t('pages.zoom.accounts.addGroup')}
                value={undefined}
                options={unassignedOptions}
                filterOption={false}
                disabled={busy}
                onSearch={setUnassignedSearch}
                onChange={(value) => assign(value, account.id)}
              />
            </AccountCard>
          );
        })}
      </AccountList>

      <UnassignedBlock>
        <SectionTitle>
          {t('pages.zoom.accounts.unassignedTitle')} ·{' '}
          {t('pages.zoom.accounts.groupsCount', {
            count: unassignedGroups.length,
          })}
        </SectionTitle>
        <Input.Search
          allowClear
          style={{ maxWidth: 320, marginBottom: 12 }}
          placeholder={t('pages.zoom.accounts.searchGroup')}
          onChange={(event) => setUnassignedSearch(event.target.value)}
        />
        {unassignedGroups.length ? (
          <UnassignedList>
            {unassignedGroups.map((group) => (
              <UnassignedRow key={group.id}>
                <GroupName>{group.name}</GroupName>
                <Select<string>
                  style={{ width: 240 }}
                  placeholder={t('pages.zoom.accounts.assignTo')}
                  value={undefined}
                  options={accountOptions}
                  disabled={busy}
                  onChange={(value) => assign(group.id, value)}
                />
              </UnassignedRow>
            ))}
          </UnassignedList>
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('pages.zoom.accounts.unassignedEmpty')}
          />
        )}
      </UnassignedBlock>
    </Wrapper>
  );
});

const Wrapper = styled.div`
  width: 100%;
  padding: 16px 0;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: 600;
  margin: 0;
`;

const AccountList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 16px;
`;

const AccountCard = styled(AntCard)<{ hasGroups: boolean }>`
  display: flex;
  flex-direction: column;
  border-left: 3px solid
    ${({ hasGroups }) => (hasGroups ? '#10b981' : '#e2e8f0')};

  .ant-card-body {
    display: flex;
    flex-direction: column;
    flex: 1;
  }
`;

const AccountHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const Dot = styled.span<{ active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ active }) => (active ? '#10b981' : '#a1a1aa')};
`;

const AccountName = styled.span`
  font-weight: 600;
`;

const CountBadge = styled.span<{ has: boolean }>`
  margin-left: auto;
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ has }) => (has ? '#047857' : '#6b7280')};
  background: ${({ has }) => (has ? '#d1fae5' : '#f1f5f9')};
`;

const AccountMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 6px;
`;

const AccountEmail = styled.span`
  color: #6b7280;
  font-size: 13px;
`;

const AccountState = styled.span`
  font-size: 13px;
`;

const AccountSynced = styled.span`
  margin-left: auto;
  font-size: 12px;
  color: #9ca3af;
`;

const GroupsLabel = styled.div`
  margin-top: 12px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const GroupChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const GroupChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 6px 3px 10px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  font-size: 13px;
`;

const ChipButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #e2e8f0;
    color: #ef4444;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const UnassignedBlock = styled.div`
  margin-top: 32px;
`;

const SectionTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 12px;
`;

const UnassignedList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const UnassignedRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 12px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  flex-wrap: wrap;
`;

const GroupName = styled.span`
  font-weight: 500;
`;

const Muted = styled.div<{ small?: boolean }>`
  color: #9ca3af;
  padding: ${({ small }) => (small ? '6px 0 0' : '24px 0')};
  font-size: ${({ small }) => (small ? '13px' : 'inherit')};
`;
